/**
 * Builds per-J-code document update payloads from a flat documents array.
 * @module generateDocumentUpdatePayloads
 */

import {
  AuthRangeResponse,
  JCodeDocuments,
} from "../../../../data-model/medicalPaOrdersModel";

/** Single payload sent to the backend for updating a J-code's documents. */
export interface JCodeDocumentUpdatePayload {
  order_id: string;
  org_id: string;
  j_code_id: string;
  documents: JCodeDocuments[];
}

/**
 * Generates one update payload per J-code from the given auth entries.
 *
 * Flattens all J-codes across auth entries and pairs each with the
 * supplied `orderId` and `orgId`, producing an array of payloads
 * ready for a bulk or sequential API call.
 *
 * @param authEntries - Auth entries containing J-codes with documents.
 * @param orderId - PA order ID to include in each payload.
 * @param orgId - Organisation ID to include in each payload.
 * @param shouldAllowEmptyDocuments - When `true`, J-codes whose `documents`
 *   array is defined but empty are still emitted (with `documents: []`),
 *   which lets callers persist a deletion that clears the last document for a
 *   J-code. Defaults to `false` so add/update flows continue to skip empty entries.
 * @returns One payload per J-code; J-codes without an `id`/`j_code` or with
 *   `undefined` documents are always skipped.
 */
export const generateDocumentUpdatePayloads = (
  authEntries: AuthRangeResponse[],
  orderId: string,
  orgId: string,
  shouldAllowEmptyDocuments: boolean = false,
): JCodeDocumentUpdatePayload[] => {
  return authEntries.flatMap((authEntry) =>
    (authEntry.j_codes ?? [])
      .filter(
        (jCode) =>
          (jCode.id || jCode.j_code) &&
          jCode.documents &&
          (jCode.documents.length > 0 || shouldAllowEmptyDocuments),
      )
      .map((jCode) => ({
        order_id: orderId,
        org_id: orgId,
        j_code_id: jCode.id ?? jCode.j_code ?? "",
        documents: jCode.documents ?? [],
      })),
  );
};

/**
 * Generates "clear documents" update payloads for every J-code that currently
 * carries a document whose `id` matches the provided `docId`.
 *
 * Each emitted payload sets `documents: []`, telling the backend to drop ALL
 * documents on that J-code (not just the matching one). This is intended for
 * flows where deleting a single document should also wipe the rest of the
 * J-code's document list (e.g. NAR-letter teardown).
 *
 * J-codes that do not carry a document with the matching `id`, or that have no
 * usable identifier (`id` / `j_code`), are skipped entirely.
 *
 * @param authEntries - Auth entries containing J-codes with documents.
 * @param orderId - PA order ID to include in each payload.
 * @param orgId - Organisation ID to include in each payload.
 * @param docId - Document `id` to look for inside each J-code's documents array.
 * @returns One `documents: []` payload per affected J-code.
 */
export const generateClearDocumentsPayloadsForDocId = (
  authEntries: AuthRangeResponse[],
  orderId: string,
  orgId: string,
  docId: string,
): JCodeDocumentUpdatePayload[] => {
  return authEntries.flatMap((authEntry) =>
    (authEntry.j_codes ?? [])
      .filter(
        (jCode) =>
          (jCode.id || jCode.j_code) &&
          (jCode.documents ?? []).some((doc) => doc.id === docId),
      )
      .map((jCode) => ({
        order_id: orderId,
        org_id: orgId,
        j_code_id: jCode.id ?? jCode.j_code ?? "",
        documents: [],
      })),
  );
};
